import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import { ArrowLeft, ArrowRight, Info, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { College, Track } from "../data/sharedTypes";
import { getCollegeNameKey } from "../data/sharedTypes";
import type { UniversityId } from "../data/universityRegistry";
import { getTrackMinHighSchool, hasSelectableSpecialties } from "../data/universityRegistry";
import StepHeader from "../StepHeader";

interface Props {
    colleges: College[];
    universityId: UniversityId;
    universityNameKey: string;
    initialTrack?: Track | null;
    onBack: () => void;
    onSelectCollege: (college: College) => void;
    onTrackChange?: (track: Track) => void;
}

export default function ChooseCollege({ colleges, universityId, universityNameKey, initialTrack, onBack, onSelectCollege, onTrackChange }: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);
    const [searchQuery, setSearchQuery] = useState("");

    const hasTracks =
        colleges.some((c) => c.track) ||
        colleges.some((c) => c.departments.some((d) => d.track));
    const defaultTrack = initialTrack ?? "scientific";
    const [activeTrack, setActiveTrack] = useState<Track>(defaultTrack);

    const handleTrackChange = (track: Track) => {
        setActiveTrack(track);
        setSearchQuery("");
        onTrackChange?.(track);
    };

    // Initialize parent with default track on mount
    useState(() => {
        if (hasTracks) onTrackChange?.(defaultTrack);
    });

    const filteredColleges = useMemo(() => {
        let list = colleges;
        if (hasTracks) {
            list = list.filter((c) => {
                // College-level track flag (KU + engineering in Internal Missions)
                if (c.track === "scientific") return activeTrack === "scientific";
                if (c.track === "literary") return activeTrack === "literary";
                // Department-level tracks (Internal Missions non-engineering):
                // only keep the college if at least one department matches.
                const deptTracks = c.departments.map((d) => d.track).filter(Boolean) as string[];
                if (deptTracks.length === 0) return true;
                return deptTracks.includes(activeTrack);
            });
        }
        const q = searchQuery.trim().toLowerCase();
        if (!q) return list;
        return list.filter((college) =>
            t(getCollegeNameKey(college)).toLowerCase().includes(q),
        );
    }, [colleges, searchQuery, t, hasTracks, activeTrack]);

    // Hide child colleges (those with parentKey) from the grid — their specialties
    // are merged into the parent's specialty list instead.
    const visibleColleges = useMemo(
        () => filteredColleges.filter((c) => !c.parentKey),
        [filteredColleges],
    );

    const getRequirementBadges = (college: College) => {
        const badges: { label: string; color: string }[] = [];
        const w = college.weights;

        // Track-aware competitive threshold (AASU Business Admin: 80% sci / 85% lit).
        // PAAET hides the per-college minimum HS badge for all colleges (both genders).
        const collegeMin =
            activeTrack === "literary" && w.minHighSchoolLiterary != null
                ? w.minHighSchoolLiterary
                : w.minHighSchool;
        if (collegeMin && universityId !== "paaet") {
            const isAasuMedicine =
                universityId === "abdullah_al_salem" && college.key === "medicine_health";
            const label = isAasuMedicine
                ? t("dream_university_aasu_min_apply", { percent: collegeMin })
                : universityId === "abdullah_al_salem"
                  ? t("dream_university_high_school_min_accepted", { percent: collegeMin })
                  : t("dream_university_high_school_min", { percent: collegeMin });
            badges.push({
                label,
                color: "bg-red-50 text-red-700",
            });
        }

        // AASU Medicine has an explicit equivalent-rate minimum on top of HS%.
        if (universityId === "abdullah_al_salem" && college.key === "medicine_health") {
            badges.push({
                label: t("dream_university_aasu_equivalent_rate_min", { percent: 90 }),
                color: "bg-red-50 text-red-700",
            });
            badges.push({
                label: t("dream_university_aasu_ielts_required"),
                color: "bg-amber-50 text-amber-700",
            });
        }

        if (w.admissionType === "high_school") {
            // Foreign Missions uses its own "Competition on Capabilities (Min.
            // Equivalent Rate)" label per MoHE terminology — FM-specific branch
            // is fully isolated.
            const label = universityId === "foreign_missions"
                ? t("dream_university_fm_competition_equivalent")
                : t("dream_university_high_school_direct");
            badges.push({
                label,
                color: "bg-blue-50 text-blue-700",
            });
        } else if (universityId === "foreign_missions") {
            // FM colleges that now use a weighted formula still need the
            // "Competition on Capabilities" chip so users understand the gate.
            badges.push({
                label: t("dream_university_fm_competition_equivalent"),
                color: "bg-blue-50 text-blue-700",
            });
        }

        if (w.additionalConditions) {
            badges.push({
                label: t(`dream_university_${w.additionalConditions}`),
                color: "bg-amber-50 text-amber-700",
            });
        }

        // For high_school colleges with no specialties, show track min HS.
        // KU Law competes purely on HS percentage with no per-college floor, so
        // suppress the badge there to avoid implying a fixed 70% / 78% cutoff.
        const suppressTrackMinBadge =
            universityId === "kuwait_university" && college.key === "law";
        if (
            w.admissionType === "high_school" &&
            !hasSelectableSpecialties(universityId, college.key) &&
            !suppressTrackMinBadge
        ) {
            const trackMin = getTrackMinHighSchool(universityId, activeTrack);
            if (trackMin) {
                badges.push({
                    label: t("dream_university_track_min_hs_badge", { percent: trackMin }),
                    color: "bg-orange-50 text-orange-700",
                });
            }
        }

        // For Internal Missions: per-specialty minimum HS (lowest cutoff across
        // the institutions that offer this specialty on the active track).
        if (universityId === "internal_missions") {
            const matching = college.departments.filter((d) => !d.track || d.track === activeTrack);
            const minPerSpecialty =
                w.admissionType === "equivalent"
                    ? matching
                          .map((d) => d.minHighSchool)
                          .filter((v): v is number => typeof v === "number")
                    : matching.map((d) => d.equivalentRate).filter((v) => v > 0);
            if (minPerSpecialty.length > 0) {
                const lowest = Math.min(...minPerSpecialty);
                badges.push({
                    label: t("dream_university_high_school_min", { percent: lowest }),
                    color: "bg-red-50 text-red-700",
                });
            }
        }

        return badges;
    };

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_choose_college")}
                subtitle={t(universityNameKey)}
            />

            {universityId === "foreign_missions" && (
                <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">
                        {t("dream_university_fm_lowest_rate_not_announced")}
                    </p>
                </div>
            )}

            {hasTracks && (
                <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
                    {(["scientific", "literary"] as Track[]).map((track) => {
                        const trackMin = getTrackMinHighSchool(universityId, track);
                        return (
                            <button
                                key={track}
                                onClick={() => handleTrackChange(track)}
                                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex flex-col items-center gap-0.5 ${
                                    activeTrack === track
                                        ? "bg-white text-theme-700 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                <span>{t(`dream_university_track_${track}`)}</span>
                                {trackMin && (
                                    <span className={`text-[10px] font-medium ${
                                        activeTrack === track ? "text-theme-600" : "text-gray-400"
                                    }`}>
                                        {t("dream_university_track_min_hs", { percent: trackMin })}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {colleges.length > 4 && (
                <div className="relative">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("dream_university_search_college")}
                        className="w-full ps-9 pe-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent placeholder:text-gray-400"
                    />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {visibleColleges.map((college) => {
                    const badges = getRequirementBadges(college);
                    return (
                        <button
                            key={college.key}
                            onClick={() => onSelectCollege(college)}
                            className="flex flex-col gap-2 p-4 bg-white border border-gray-200 rounded-xl hover:border-theme-500 hover:shadow-md transition-all duration-200 text-start"
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{college.emoji}</span>
                                <span className="text-sm font-medium text-gray-800">
                                    {t(getCollegeNameKey(college))}
                                </span>
                            </div>
                            {badges.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {badges.map((badge, i) => (
                                        <span
                                            key={i}
                                            className={`text-[10px] px-2 py-0.5 rounded-full ${badge.color}`}
                                        >
                                            {badge.label}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
